import { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useOpenEntityDetailOptional } from '../contexts/OpenEntityDetailContext';
import { handleEntityDetailActivation } from '../utils/entity-detail-activation';
import {
  entityDetailHref,
  relatedEntityFromMentionEvent,
} from '../utils/entity-utils';

type UseMentionClickNavigationOptions = {
  onActivate?: () => void;
};

export function useMentionClickNavigation(
  options: UseMentionClickNavigationOptions = {}
): void {
  const { projectId } = useParams<{ projectId?: string }>();
  const navigate = useNavigate();
  const entityDetail = useOpenEntityDetailOptional();
  const projectIdRef = useRef(projectId);
  projectIdRef.current = projectId;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const openEntityDetailRef = useRef(entityDetail?.openEntityDetail);
  openEntityDetailRef.current = entityDetail?.openEntityDetail;
  const onActivateRef = useRef(options.onActivate);
  onActivateRef.current = options.onActivate;

  useEffect(() => {
    const onClick = (event: MouseEvent): void => {
      const currentProjectId = projectIdRef.current;
      if (!currentProjectId) {
        return;
      }
      const entity = relatedEntityFromMentionEvent(event);
      if (!entity) {
        return;
      }
      const href = entityDetailHref(entity, currentProjectId);
      if (!href) {
        return;
      }
      onActivateRef.current?.();
      const handled = handleEntityDetailActivation(
        event,
        entity,
        currentProjectId,
        openEntityDetailRef.current
      );
      if (handled) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      if (event.button === 0) {
        navigateRef.current(href);
      }
    };

    document.addEventListener('click', onClick, true);
    document.addEventListener('auxclick', onClick, true);
    return () => {
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('auxclick', onClick, true);
    };
  }, []);
}
